import { useEffect, useState } from "react";
import {
  Zap,
  FileText,
  MessageSquare,
  ChevronUp,
  ArrowRight,
  Play,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { DATE_TIME_FORMAT } from "@/v2/assets/utils";
import dayjs from "dayjs";
import { AiOutlineExclamationCircle } from "react-icons/ai";

type InteractionEventType =
  | "Agent Assignment"
  | "Status Change"
  | "Category Update"
  | "Topic Update"
  | "Ticket Skipped"
  | "Ticket Escalated"
  | "Ticket Answered by Quack"
  | "Assigned to Agent"
  | "Autonomous Action"
  | "Ticket Created"
  | "User Comment"
  | "Agent Comment"
  | "Skill Used"
  | "Skill Run Failed";

export type TrackerItemEventType =
  | "Comment Added"
  | "Stage Changed"
  | "Column Changed"
  | "Captured by Tracker"
  | "Tracker Event";

export interface ActivityEvent {
  type: InteractionEventType | TrackerItemEventType;
  timestamp: string; // ISO string
  details?: {
    previousValue?: string;
    newValue?: string;
    fromStatus?: string;
    toStatus?: string;
    fromAgent?: string;
    toAgent?: string;
    fromTopic?: string;
    toTopic?: string;
    fromCategory?: string;
    toCategory?: string;
    assignedTo?: string;
    username?: string;
    escalationReason?: string;
    enrichment?: string;
    decision?: string;
    reason?: string;
  };
  trackerDetails?: {
    trackerName?: string;
    trackerId?: string;
    toColumn?: string;
    toStage?: string;
    fromStage?: string;
    toStageReason?: string;
    toStageDescription?: string;
    comment?: string;
  };
}

interface ActivityTimelineProps {
  events: ActivityEvent[];
  onUpdate?: (events: ActivityEvent[]) => void;
}

export default function ActivityTimeline({
  events: initialEvents,
  onUpdate,
}: ActivityTimelineProps) {
  const [events, setEvents] = useState<ActivityEvent[]>(initialEvents);

  // Sort events by timestamp (newest first)
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  // Update the component when new events are added
  useEffect(() => {
    setEvents(initialEvents);
    if (onUpdate) {
      onUpdate(initialEvents);
    }
  }, [initialEvents, onUpdate]);

  // Format timestamp based on recency
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 5) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else {
      return dayjs(date).format(DATE_TIME_FORMAT);
    }
  };

  // Get icon based on event type
  const getEventIcon = (type: InteractionEventType | TrackerItemEventType) => {
    switch (type) {
      case "Status Change":
        return <Zap className="w-3 h-3" />;
      case "Agent Assignment":
      case "Assigned to Agent":
        return <ArrowRight className="w-3 h-3" />;
      case "Ticket Created":
      case "Ticket Answered by Quack":
      case "User Comment":
      case "Agent Comment":
        return <MessageSquare className="w-3 h-3" />;
      case "Autonomous Action":
        return <ChevronUp className="w-3 h-3" />;
      case "Skill Used":
        return <Play size={14} />;
      case "Skill Run Failed":
        return <AiOutlineExclamationCircle />;
      default:
        return <FileText className="w-3 h-3" />;
    }
  };

  // Get title based on event type
  const getEventTitle = (event: ActivityEvent) => {
    return event.type;
  };

  // Get description based on event type
  const getEventDescription = (event: ActivityEvent) => {
    switch (event.type) {
      case "Status Change":
        return event.details?.previousValue
          ? `${event.details.previousValue} → ${event.details.newValue}`
          : `→ ${event.details?.newValue}`;
      case "Agent Assignment":
        return event.details?.previousValue
          ? `${event.details.previousValue} → ${event.details.newValue}`
          : `→ ${event.details?.newValue}`;
      case "Topic Update":
        return event.details?.previousValue
          ? `${event.details.previousValue} → ${event.details.newValue}`
          : `→ ${event.details?.newValue}`;
      case "Category Update":
        return event.details?.previousValue
          ? `${event.details.previousValue} → ${event.details.newValue}`
          : `→ ${event.details?.newValue}`;
      case "Ticket Escalated":
      case "Ticket Skipped":
      case "Autonomous Action":
        return event.details?.reason || null;
      case "User Comment":
      case "Agent Comment":
        return event.details?.newValue || null;
      case "Comment Added":
        return event.trackerDetails?.comment || null;
      case "Column Changed":
        return event.trackerDetails?.toColumn
          ? `Moved to ${event.trackerDetails.toColumn}`
          : null;
      case "Stage Changed":
        return event.trackerDetails?.toStage
          ? `Moved to ${event.trackerDetails.toStage} ${
              event.trackerDetails.toStageDescription
                ? `due to ${event.trackerDetails.toStageDescription}`
                : ""
            }`
          : null;
      case "Skill Used":
      case "Skill Run Failed":
        return event.details?.newValue
          ? JSON.parse(event.details?.newValue).join(", ") || null
          : null;
      default:
        return null;
    }
  };

  return (
    <div className="p-0.5 rounded-md">
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-3 top-3 bottom-0 w-px bg-border z-0"></div>

        {/* Timeline events */}
        <div className="space-y-2">
          {sortedEvents.map((event, index) => (
            <div
              key={`${event.type}-${index}-${event.timestamp}`}
              className="relative flex gap-1"
            >
              {/* Icon */}
              <div className="z-10 flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background border border-border">
                {getEventIcon(event.type)}
              </div>

              {/* Content */}
              <div className="flex-1 pt-0.5">
                <div className="flex items-baseline">
                  <h3 className="text-sm font-medium">
                    {getEventTitle(event)}
                  </h3>
                  <span className="ml-1 text-muted-foreground text-xs">
                    {formatTimestamp(event.timestamp)}
                  </span>
                </div>

                {getEventDescription(event) && (
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {getEventDescription(event)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
